import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import SectionHeading from './SectionHeading';
import AdvantageGlyph from './AdvantageGlyph';
import Icon from './Icon';
import { whyUs, companyLead } from '../content/company';

/**
 * Чому обирають VMG.
 *
 * Перелік переваг складається з тверджень, які можна перевірити: одна
 * спеціалізація, всі напрямки разом, партнери в суміжних галузях, дистанційна
 * робота, готовність сказати «вам це не потрібно». Фраз на кшталт
 * «індивідуальний підхід» тут немає — вони стоять на сайті будь-якої
 * юридичної фірми.
 *
 * Оформлення — сітка з шести плиток три на два. Перша — фото під темним
 * градієнтом із тезою секції; решта п'ять — по перевазі на плитку, у кожної
 * власний малюнок (AdvantageGlyph) замість галочки. Попередні дві редакції
 * — п'ять однакових плиток збоку від абзацу, спершу на чорному фото, потім
 * на білому — власник назвав нецікавими: п'ять разів та сама плитка з
 * галочкою і є нецікаво.
 *
 * Плитки однакового розміру навмисно: різні розміри в такій сітці читалися
 * б як «ця перевага важливіша», а всі п'ять рівнозначні. Фото — постер
 * шостого відео (лікар із планшетом): дистанційна робота — одна з п'яти
 * переваг, і кадр саме про неї; файл уже на сервері.
 */
const WhyChooseUs = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section id="why-choose-us" className="py-16 lg:py-24 bg-white" ref={ref}>
      <div className="section-container">
        <motion.div
          className="mb-10 lg:mb-14"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <SectionHeading
            title="Переваги"
            caption="Юридичний супровід клінік і лікарів"
            description="Чому обирають VMG: п’ять тверджень про те, як ми працюємо. Кожне з них можна перевірити вже в першій розмові."
            badge={{ value: 'VMG', label: 'Visia Medical Group' }}
          />
        </motion.div>

        <ul className="list-none grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          <motion.li
            className="relative overflow-hidden rounded-2xl bg-ink min-h-[16rem] flex"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <img
              src={process.env.PUBLIC_URL + '/images/team-1e8282df-sm.jpg'}
              srcSet={`${process.env.PUBLIC_URL}/images/team-1e8282df-sm.jpg 800w, ${process.env.PUBLIC_URL}/images/team-1e8282df.jpg 1600w`}
              sizes="(min-width: 1280px) 400px, (min-width: 768px) 50vw, 100vw"
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-deep via-ink-deep/50 to-ink-deep/10" aria-hidden="true"></div>
            <div className="relative mt-auto p-6 lg:p-7">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-px bg-gradient-to-r from-teal-400 to-sky-400 flex-shrink-0"></span>
                <span className="text-teal-300 uppercase tracking-[0.2em] text-[0.7rem] font-semibold">
                  Visia Medical Group
                </span>
              </div>
              <p className="text-lg lg:text-xl font-display font-semibold text-white leading-snug">
                {companyLead[2]}
              </p>
            </div>
          </motion.li>

          {whyUs.map((point, index) => (
            <motion.li
              key={point}
              className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 lg:p-7 flex flex-col transition-[border-color,background-color] duration-300 hover:border-teal-300 hover:bg-white"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.08 }}
            >
              <div className="flex items-start justify-between">
                <AdvantageGlyph index={index} className="w-11 h-11 text-teal-700" />
                <span className="text-xs font-bold tracking-[0.2em] text-slate-500">
                  {String(index + 1).padStart(2, '0')}
                </span>
              </div>
              <p className="mt-8 text-slate-800 leading-relaxed">{point}</p>
            </motion.li>
          ))}
        </ul>

        <Link
          to="/pro-kompaniyu"
          className="mt-10 inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800 transition-colors duration-200"
        >
          Як влаштована робота з нами
          <Icon name="fa-arrow-right" />
        </Link>
      </div>
    </section>
  );
};

export default WhyChooseUs;
